import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AddPositionModal } from '@/components/positions/AddPositionModal'
import { primaryButtonClass } from '@/components/common/formStyles'
import { listAccounts } from '@/lib/accountsApi'
import { listPositions } from '@/lib/positionsApi'
import type { Account } from '@/types/account'

export function Step3Positions() {
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts })
  const positionsQuery = useQuery({ queryKey: ['positions'], queryFn: listPositions })
  const [modalOpen, setModalOpen] = useState(false)

  const tradeableAccounts: Account[] = (accountsQuery.data ?? []).filter(
    (a) => a.broker_type !== 'external',
  )
  const positions = positionsQuery.data ?? []

  const accountById = new Map(tradeableAccounts.map((a) => [a.id, a]))
  const visiblePositions = positions.filter((p) => accountById.has(p.account_id))

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="tracked-caps text-sm text-ink">Bestehende Positionen erfassen</h2>
        <p className="mt-3 max-w-2xl font-mono text-sm leading-6 text-muted">
          Erfasse pro Broker-Account die Positionen, die du heute schon hältst — mit ISIN und
          mindestens einer Tax-Lot. Wenn du keine Daten zur Hand hast, kannst du diesen Schritt
          überspringen und später per CSV-Import nachholen (Phase 1b).
        </p>
      </div>

      <section className="hairline-y py-5">
        <div className="mb-4 flex items-baseline justify-between">
          <p className="tracked-caps text-[10px] text-faint">
            {visiblePositions.length === 0
              ? 'Noch keine Positionen'
              : `${visiblePositions.length} Position(en)`}
          </p>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={tradeableAccounts.length === 0}
            className={primaryButtonClass}
          >
            + Position hinzufügen
          </button>
        </div>

        {tradeableAccounts.length === 0 && (
          <p className="font-mono text-sm text-faint">
            Keine handelbaren Accounts (IBKR, Consors, Scalable, manuell). Lege in Schritt 2
            mindestens einen an oder überspringe diesen Schritt.
          </p>
        )}

        {visiblePositions.length > 0 && (
          <ul className="flex flex-col">
            {visiblePositions.map((pos) => {
              const account = accountById.get(pos.account_id)
              return (
                <li
                  key={pos.id}
                  className="hairline-b flex items-baseline justify-between py-2.5 font-mono text-sm"
                >
                  <span className="text-ink">{pos.name}</span>
                  <span className="flex items-baseline gap-3 text-muted">
                    <span className="text-faint">{pos.isin}</span>
                    {account && (
                      <span className="tracked-caps text-[10px] text-bernstein">
                        {account.name}
                      </span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <AddPositionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        accounts={tradeableAccounts}
        onNeedAccount={() => setModalOpen(false)}
      />
    </div>
  )
}
